import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { LogIn, User, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export const Login = () => {
    const [username, setUsername] = useState(localStorage.getItem('remembered_user') || '');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [remember, setRemember] = useState(!!localStorage.getItem('remembered_user'));
    const { login } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const result = await login(username, password);
        if (result.success) {
            if (remember) localStorage.setItem('remembered_user', username);
            else localStorage.removeItem('remembered_user');
            toast.success('Bienvenido');
        } else {
            setError(result.error || 'Usuario o contrasena incorrectos');
            toast.error('Usuario o contrasena incorrectos');
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
            <div className="absolute inset-0 opacity-30" style={{
                backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(251,191,36,0.1) 0%, transparent 50%)'
            }} />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="relative w-full max-w-sm">
                <div className="text-center mb-8">
                    <img src="/logo-karry.png" alt="Karry Jean's" className="w-20 h-20 rounded-2xl mx-auto mb-4 object-contain" />
                    <h1 className="font-display text-3xl tracking-wider text-white">KARRY JEAN'S</h1>
                    <p className="text-zinc-500 text-sm mt-2">Sistema de Producción Textil</p>
                </div>

                <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                {error}
                            </div>
                        )}
                        <div>
                            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">Usuario</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                <Input
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Ingrese su usuario"
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-400"
                                    data-testid="input-username"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">Contraseña</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Ingrese su contraseña"
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-400"
                                    data-testid="input-password"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input type="checkbox" id="remember" checked={remember} onChange={e => setRemember(e.target.checked)}
                                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-400 focus:ring-amber-400" />
                            <label htmlFor="remember" className="text-sm text-zinc-400 cursor-pointer">Recordar usuario</label>
                        </div>
                        <Button type="submit" disabled={loading}
                            className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-900 font-semibold h-11 rounded-xl"
                            data-testid="login-submit-btn">
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-zinc-900/30 border-t-zinc-900 rounded-full animate-spin" />
                            ) : (
                                <><LogIn className="w-4 h-4 mr-2" /> Iniciar Sesión</>
                            )}
                        </Button>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};
